import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Form, Button, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import QueueTable from '../components/QueueTable';
import { queueApi, spotifyApi } from '../api/api';
import { useQueueUpdates } from '../hooks/useQueueUpdates';

export default function Queue() {
  const [queue, setQueue] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchQueue = useCallback(
    () => queueApi.list().then((r) => setQueue(r.data)).catch(() => {}),
    []
  );

  // Real-time queue updates via WebSocket
  useQueueUpdates(fetchQueue);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 10000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    try {
      const { data } = await spotifyApi.search(search);
      setResults(data.tracks || []);
    } catch {
      toast.error('Suche fehlgeschlagen');
    } finally {
      setSearching(false);
    }
  };

  const handleAddToQueue = async (track) => {
    try {
      await queueApi.add({
        spotify_uri: track.uri,
        title: track.title,
        artist: track.artist,
        album: track.album,
        cover_url: track.cover_url,
      });
      toast.success(`'${track.title}' zur Queue hinzugefügt`);
      fetchQueue();
      setResults([]);
      setSearch('');
    } catch {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleRemove = async (id) => {
    await queueApi.remove(id);
    fetchQueue();
  };

  const handleSkip = async () => {
    await queueApi.skip();
    toast.info('Song geskippt');
    fetchQueue();
  };

  const fmt = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  return (
    <div>
      <h4 className="text-white mb-4">🎵 Song Queue</h4>

      <Row className="g-4">
        {/* Search */}
        <Col lg={5}>
          <Card bg="dark" text="white" className="border-secondary">
            <Card.Body>
              <Card.Title>Song suchen & hinzufügen</Card.Title>
              <Form onSubmit={handleSearch}>
                <InputGroup className="mb-3">
                  <Form.Control
                    placeholder="Spotify suchen…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-dark text-white border-secondary"
                  />
                  <Button type="submit" variant="success" disabled={searching}>
                    {searching ? '…' : '🔍'}
                  </Button>
                </InputGroup>
              </Form>
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {results.map((track) => (
                  <div
                    key={track.uri}
                    className="d-flex align-items-center gap-2 p-2 mb-1 rounded"
                    style={{ background: '#1a1a2e', cursor: 'pointer' }}
                    onClick={() => handleAddToQueue(track)}
                  >
                    {track.cover_url && (
                      <img src={track.cover_url} alt="" style={{ width: 40, height: 40, borderRadius: 4 }} />
                    )}
                    <div className="overflow-hidden flex-grow-1">
                      <div className="text-white text-truncate small fw-semibold">{track.title}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: 12 }}>{track.artist}</div>
                    </div>
                    <div className="text-muted small">{fmt(track.duration_ms)}</div>
                    <Button variant="success" size="sm">+</Button>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Queue */}
        <Col lg={7}>
          <Card bg="dark" text="white" className="border-secondary">
            <Card.Body>
              <Card.Title>Aktuelle Queue ({queue.length})</Card.Title>
              <QueueTable queue={queue} onRemove={handleRemove} onSkip={handleSkip} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
